/**
 * Workflow Type System for Gibbs Action Registry
 *
 * Defines all data types from the drug discovery task catalog glossary
 * and provides type compatibility checking for workflow composition.
 *
 * @module gibbs/registry/types
 */

// ─── Workflow Type Constants ─────────────────────────────────────────────────

/** @enum {string} */
export const WorkflowTypes = Object.freeze({
    ProteinStructure: 'ProteinStructure',
    CaseData: 'CaseData',
    Compound: 'Compound',
    CompoundList: 'CompoundList',
    Fragment: 'Fragment',
    FragmentMap: 'FragmentMap',
    FragmentSearchResult: 'FragmentSearchResult',
    SMILES: 'SMILES',
    SDF: 'SDF',
    PDB: 'PDB',
    CIF: 'CIF',
    MolData: 'MolData',
    BindingSite: 'BindingSite',
    DockingBox: 'DockingBox',
    EnergyBreakdown: 'EnergyBreakdown',
    PropertySet: 'PropertySet',
    AtomSelection: 'AtomSelection',
    WorkspaceState: 'WorkspaceState',
    JobId: 'JobId',
    CSV: 'CSV',
    ScoreList: 'ScoreList',
    SimSpec: 'SimSpec',
    BondVector: 'BondVector',
    PdbData: 'PdbData',
    FoldingInput: 'FoldingInput',
    QueryString: 'QueryString',
});

// ─── Type Descriptions ───────────────────────────────────────────────────────

/** @type {Object<string, string>} */
const typeDescriptions = Object.freeze({
    ProteinStructure: '3D protein coordinates loaded as a MapCase',
    CaseData: 'Protein case with associated metadata, fragments, ligands',
    Compound: 'A molecule with 3D coordinates, properties, and heritage',
    CompoundList: 'Array of Compound objects',
    Fragment: 'Medicinal chemistry fragment with binding data and pose statistics',
    FragmentMap: 'Full fragment library for a protein target',
    FragmentSearchResult: 'Ranked fragment suggestions from a search',
    SMILES: 'Simplified molecular-input line-entry system string',
    SDF: 'Structure Data File with 3D coordinates and properties',
    PDB: 'Protein Data Bank format coordinates',
    CIF: 'Crystallographic Information File',
    MolData: 'Raw molecular structure in any supported format (sdf, mol, mol2, pdb, smi, inchi, cdx, cdxml, cml, xyz)',
    BindingSite: 'Protein binding cavity definition (center XYZ + radius + residue list)',
    DockingBox: '3D search space for docking (center XYZ + size XYZ)',
    EnergyBreakdown: 'Detailed energy components (vdw, electrostatic, solvation, stress, total) in kcal/mol',
    PropertySet: 'Key-value map of computed properties',
    AtomSelection: 'Set of selected atoms/residues (server-side selection state)',
    WorkspaceState: 'Serialized workspace snapshot (JSON SavedState)',
    JobId: 'Reference to an async distributed job (string)',
    CSV: 'Tabular data with headers',
    ScoreList: 'Ranked compounds with scores and properties',
    SimSpec: 'Simulation specification (proteins, ligands, system setup)',
    BondVector: 'Pair of atoms defining a growth direction [Atom, Atom]',
    PdbData: 'Parsed PDB with metadata and formatted PDB string',
    FoldingInput: 'Protein sequence(s) and optional ligands for folding',
    QueryString: 'Atom selection query (e.g., "protein within 5 ligand")',
});

// ─── Type Compatibility Rules ────────────────────────────────────────────────

/**
 * Declarative compatibility rules. Each entry maps an output type to
 * the set of input types it can satisfy.
 *
 * Rules:
 * - Exact match: every type is compatible with itself
 * - Array coercion: Compound → CompoundList (single wrapped in array)
 * - Format subsumption: SDF, PDB, CIF, SMILES, etc. → MolData
 * - Structural: ProteinStructure → PDB (can export as PDB)
 *
 * @type {Object<string, string[]>}
 */
const compatibilityRules = Object.freeze({
    // Array coercion
    Compound: ['Compound', 'CompoundList'],
    Fragment: ['Fragment'],
    CompoundList: ['CompoundList'],

    // Format → MolData subsumption
    SDF: ['SDF', 'MolData'],
    PDB: ['PDB', 'MolData'],
    CIF: ['CIF', 'MolData'],
    SMILES: ['SMILES', 'MolData'],
    MolData: ['MolData'],

    // Structural conversions
    ProteinStructure: ['ProteinStructure', 'PDB'],
    PdbData: ['PdbData', 'PDB'],
});

/**
 * Check whether an output type from one action can feed an input type
 * of another action.
 *
 * @param {string} outputType - The type produced by the upstream action
 * @param {string} inputType  - The type expected by the downstream action
 * @returns {boolean} True if compatible
 */
export function isTypeCompatible(outputType, inputType) {
    // Exact match always works
    if (outputType === inputType) return true;

    // Check explicit compatibility rules
    const compatibleWith = compatibilityRules[outputType];
    if (compatibleWith && compatibleWith.includes(inputType)) return true;

    return false;
}

/**
 * Get the human-readable description of a workflow type.
 *
 * @param {string} typeName - A WorkflowTypes constant
 * @returns {string|undefined} Description or undefined if unknown type
 */
export function getTypeDescription(typeName) {
    return typeDescriptions[typeName];
}

/**
 * Get all workflow types with their descriptions. Useful for serializing
 * to LLM context.
 *
 * @returns {Array<{type: string, description: string}>}
 */
export function getAllTypes() {
    return Object.entries(typeDescriptions).map(([type, description]) => ({
        type,
        description,
    }));
}

/**
 * Get the compatibility rules table. Useful for serializing to LLM context.
 *
 * @returns {Object<string, string[]>}
 */
export function getCompatibilityRules() {
    return compatibilityRules;
}
